import { Toast } from 'antd-mobile'
import { divide, times } from 'number-precision'
import { omit } from 'lodash'

import i18n from '@/i18n'
import { translateCurrency } from '@/utils'
import { SHIPMENT_PICKUP_TYPE } from './enum'
import { ProviderInfoItem, ExpressListData } from './types'
import { SAAS_FLAG, DELIVERY_SETTLE, PAY_TYPE, DEFAULT_PAYMENT_OPTIONS } from '@/constants'
import { getFeeList, getSubAccount, getExpressList, queryExpressConfig, FeeObject, FeePayloadMetadatasItem } from '@/services/api'
import { PaymentTypeEnum } from './enum'

import type { IExpressProps, IServiceProviderVO, IProviderAddressVO, ICoverAddressTypes, IAddressItem } from './types'
import { ExpressTypeConfigListRequest } from '@/services/api/type'
import { isZEEKFn } from '@/utils/form/serviceProvider'
import { ParcelFormFields } from '../parcelSlice/types'
import { DELIVER_VALUE_MAP } from './utils/feeItem'

export const getPickupType = (options: { supportedPickupTypes: SHIPMENT_PICKUP_TYPE[]; provider_pickup_type?: number }) => {
  const { supportedPickupTypes, provider_pickup_type } = options
  // the priority is the pickup
  const defaultPickupType = supportedPickupTypes?.includes(SHIPMENT_PICKUP_TYPE.pickUp) ? SHIPMENT_PICKUP_TYPE.pickUp : supportedPickupTypes?.[0]
  const pickupType = supportedPickupTypes?.includes(provider_pickup_type as SHIPMENT_PICKUP_TYPE) ? provider_pickup_type : defaultPickupType

  return pickupType
}

export const logger = (msg: string, type: 'warn' | 'log' | 'error' | 'info' = 'warn') => {
  // eslint-disable-next-line no-console
  console[type]('[service provider]: ', msg)
}

/**
 * 获取 cod 选项
 * https://shopline.yuque.com/mlu41d/gy9ldi/ezg64m#Xd86c 详细介绍
 * @param data
 * @returns
 */
export const getShipmentServiceType = (
  data: { supportedCheckoutTypes: number[]; filterArr?: string[]; previousShipmentServiceType?: number } & Pick<IServiceProviderVO, 'type'>
) => {
  const { supportedCheckoutTypes, filterArr, previousShipmentServiceType, type } = data

  // 有筛选项，选择了货到付款
  if (filterArr?.length && filterArr.includes(String(PaymentTypeEnum.cod))) {
    return PaymentTypeEnum.cod
  }

  // 如果只支持一项
  if (supportedCheckoutTypes.length === 1) {
    return supportedCheckoutTypes[0]
    // 没有上一次选项，且服务产品支持2个选项，类型为合单发货的，为空
  } else if (!previousShipmentServiceType && type === 'merge') {
    return undefined
    // 没有上一次选项，切有2个选项的，默认非 cod
  } else if (!previousShipmentServiceType) {
    return PaymentTypeEnum.notCod
    // 有上一次的选项为 cod，且产品支持 cod
  } else if (previousShipmentServiceType === PaymentTypeEnum.cod && supportedCheckoutTypes.includes(PaymentTypeEnum.cod)) {
    return PaymentTypeEnum.cod
  }

  return PaymentTypeEnum.notCod
}

/**
 * 处理 cod 选项
 * @param data
 * @returns
 */
export const getCodFee = (
  data: {
    provider_payment_type: number
    /**
     * 订单金额
     */
    orderCodAmount?: number
    previousCodFee?: number | string
  } & Pick<IServiceProviderVO, 'type'>
) => {
  const { provider_payment_type, type, orderCodAmount, previousCodFee } = data

  if (provider_payment_type === PaymentTypeEnum.notCod || type === 'merge') {
    return ''
  } else if (orderCodAmount) {
    // 拥有修改的值就读修改后的值否则读订单原本
    if (!isNaN(Number(previousCodFee))) {
      return previousCodFee
    } else {
      const codFee = divide(Number(orderCodAmount || 0), 100)
      return codFee
    }
  }
  // 如果为创建运单或者编辑运单，则直接读取上一次的编辑的值

  return previousCodFee
}

/**
 * 获取 payment
 * @param data
 * @returns
 */
export const handlePayment = (data: {
  companyCode: string
  settlementModes: number[]
  estimatedCost: number
  isPrepaid: boolean
  type: IServiceProviderVO['type']
  saasFlag: number
  /** 用户钱包的币种没有该服务产品的运费币种 */
  freightCurrencyCodeNoInUserCurrencyCodeList: boolean
}) => {
  const { companyCode, settlementModes, estimatedCost, isPrepaid, saasFlag, type, freightCurrencyCodeNoInUserCurrencyCodeList } = data
  const freight = estimatedCost || 0

  // 到付现结 根据接口（后端根据 Onship服务产品授权）
  // 改动 PRD https://shopline.yuque.com/mlu41d/gy9ldi/evvics
  // 数据直接读取后端
  let payTypeOptions =
    settlementModes
      ?.map((value) => ({
        value,
        label: PAY_TYPE[value],
        disabled: DEFAULT_PAYMENT_OPTIONS[value],
      }))
      .sort((a, b) => a.value - b.value) || []

  // 到付现结的option位置
  const cashOnDeliveryItemInd = payTypeOptions.findIndex((item) => item.value === 2)

  // zeek 特殊处理， 排除saas服务产品
  if (type !== 'batch' && ['ZEEK', 'HKZEEK'].indexOf(companyCode) !== -1 && saasFlag !== SAAS_FLAG.YES) {
    // 预估运费没问题
    if (freight !== 0) {
      // 再根据是否支持加入 到付现结
      if (-1 !== settlementModes?.indexOf(2)) {
        payTypeOptions[cashOnDeliveryItemInd].disabled = false
      }
    }
  } else {
    // 根据是否支持加入 到付现结
    if (-1 !== settlementModes?.indexOf(2)) {
      payTypeOptions[cashOnDeliveryItemInd].disabled = false
    }
  }

  // 有寄付就寄付，没寄付就选择第一个可用的选项
  let defaultValue = DELIVERY_SETTLE

  // 用户币种钱包没有此服务产品运费币种，只支持【到付现结】2(因为到付现结是线下给运费的，不扣钱包钱)
  // 算不出运费，也只支持【到付现结】
  // prd: https://shopline.yuque.com/mlu41d/gy9ldi/bbntsl#NGkpY
  if (saasFlag !== SAAS_FLAG.YES && isPrepaid && type !== 'batch') {
    // 注意step by step订单编辑场景，此方法在初始化执行的时候，会出现服务产品没有币种的情况
    // 此时options会依赖props的onlyShowCashOnDelivery属性，在PaymentFormItem组件处理
    if (freightCurrencyCodeNoInUserCurrencyCodeList || freight === 0) {
      payTypeOptions = payTypeOptions.map((item) => ({
        ...item,
        disabled: item.value !== 2,
      }))
      defaultValue = undefined // 支付方式默认值留空
    }
  }

  const enabledOptions = payTypeOptions.filter((item) => !item.disabled) // 可选的options
  const enabledValues = enabledOptions.map((item) => item.value) // 可选的options的值
  defaultValue = defaultValue === undefined || enabledValues.includes(defaultValue) ? defaultValue : enabledValues[0]

  return {
    options: enabledOptions,
    defaultValue,
    list: enabledValues,
  }
}

export interface GetPickupTimeOptions {
  supportedPickupTypes: number[]
  formValues?: {
    provider_pickup_type?: number
    provider_pickup_date?: string
    provider_pickup_time_slots?: string
    provider_pickup_dates?: string[]
  }
}

export interface PriceSettingInfo {
  allAssociatedService?: boolean
  pickupType?: number[]
  deliveryType?: number[]
}

/**
 * 服务产品配置(获取揽收时间和税号配置)
 */
export const getExpressConfig = async ({
  supportedPickupTypes,
  formValues,
  expressTypeCode,
  address,
  priceSettingInfo,
  type,
}: GetPickupTimeOptions &
  ExpressTypeConfigListRequest & {
    priceSettingInfo?: PriceSettingInfo
    type: IServiceProviderVO['type']
  }) => {
  const { data } = await queryExpressConfig({
    expressTypeCode,
    address,
  })

  const canPickUp = supportedPickupTypes?.includes(SHIPMENT_PICKUP_TYPE.pickUp)

  let { provider_pickup_type } = formValues || {}
  const { provider_pickup_dates = [] } = formValues || {}
  let [provider_pickup_date, provider_pickup_time_slots] = provider_pickup_dates
  // https://shopline.yuque.com/mlu41d/gy9ldi/nqvrv8xu75x4g9m2#oTw3M 默认逻辑修改
  const defaultPickup = -1 !== supportedPickupTypes?.indexOf(SHIPMENT_PICKUP_TYPE.pickUp) ? SHIPMENT_PICKUP_TYPE.pickUp : SHIPMENT_PICKUP_TYPE.dropOff
  let list: ProviderInfoItem[] = []
  let shipmentPickupTimeRangOptions: string[] = []

  // 能不能 pickup 都不影响 formValues 的值
  // formValues 中的值永远不能被修改(*最高优先级*)
  // 只要服务产品支持 pickup 就直接请求一次, 后续不会请求, 除非切换服务产品
  if (canPickUp) {
    list = data.selectablePickTimes || []
    if (list.length) {
      // 此处主要用于编辑场景
      // 默认有值则需要查找对应的项赋值给 shipmentPickupTimeRangOptions
      // 其他值不能进行赋值操作, 否则会改变原表单信息
      if (formValues && type !== 'create') {
        const selected = list.find(({ date }) => date === provider_pickup_date)
        selected && (shipmentPickupTimeRangOptions = selected?.timeIntervals)
      } else {
        // 初始化默认逻辑
        // 如果表单没有值, 则需要帮忙设置默认值
        const [selected] = list
        shipmentPickupTimeRangOptions = selected.timeIntervals
        provider_pickup_date = selected.date
        provider_pickup_time_slots = shipmentPickupTimeRangOptions?.[0]
      }
    }
  }

  // 计价匹配
  // 优先使用交集
  // 没有交集走原本选择
  if (priceSettingInfo && !priceSettingInfo?.allAssociatedService && priceSettingInfo?.pickupType?.length) {
    provider_pickup_type = getPickupType({
      supportedPickupTypes: priceSettingInfo?.pickupType,
      provider_pickup_type,
    })
  }

  provider_pickup_type = provider_pickup_type || defaultPickup

  return {
    dateData: {
      shipmentPickupDateOptions: list,
      shipmentPickupTimeRangOptions,
      provider_pickup_time_slots,
      provider_pickup_dates: [provider_pickup_date, provider_pickup_time_slots].filter(Boolean),
      provider_pickup_type: provider_pickup_type,
    },
    extraConfig: data.configs,
    supportStationPickUp: data?.supportStationPickUp,
  }
}

// 获取 options 上面已经做过了
export const initialPickupTimeWithChange = (
  provider_pickup_type: number,
  options: {
    providerDateOptions: ProviderInfoItem[]
  }
) => {
  const { providerDateOptions } = options

  // 如果不是预约揽收或者无数据
  if (provider_pickup_type !== SHIPMENT_PICKUP_TYPE.pickUp || !providerDateOptions?.length) {
    return {
      provider_pickup_dates: [],
      provider_pickup_time_slots: '',
      provider_pickup_date: '',
    }
  }

  const defaultData = providerDateOptions[0]
  const provider_pickup_date = defaultData.date
  const providerTimeOptions = defaultData?.timeIntervals
  const provider_pickup_time_slots = providerTimeOptions?.[0]

  return {
    providerTimeOptions,
    provider_pickup_dates: [provider_pickup_date, provider_pickup_time_slots].filter(Boolean),
    provider_pickup_time_slots,
    provider_pickup_date,
  }
}

export const handleSubAccount = async ({ expressTypeCode, pickupType }: { expressTypeCode: string; pickupType: number }) => {
  const data = await getSubAccount({ expressTypeCode, pickupType: pickupType as any })
  const list = data?.data?.subAccountDetails || []

  return {
    list,
    value: list?.[0]?.accountNo,
  }
}

type IGetServiceProvider = {
  currencyCode: string
  isPrepaid?: boolean
  props?: IExpressProps & {
    curExpTypeCode?: string
  }
  orderCodCurrencyCode?: string
  provider_pickup_type?: number
  partitionInfo?: Partial<FeePayloadMetadatasItem['partitionInfo']>
} & Pick<IServiceProviderVO, 'type'>

/**
 * format 地址
 * @param payload
 * @returns
 */
export function convertAddress(payload: Pick<IExpressProps, 'addressVO'> & Pick<IServiceProviderVO, 'type'> & { isHome?: boolean }) {
  const { addressVO, type, isHome } = payload
  interface IAddress {
    senderAddressName?: IAddressItem
    consigneeAddressName?: IAddressItem
  }
  const address: IAddress = {}

  address['senderAddressName'] = {
    countryCode: addressVO?.departure_countryCode,
    provinceName: addressVO?.departure_province,
    cityName: addressVO?.departure_city,
    districtName: addressVO?.departure_district,
    zipCode: addressVO?.departure_zipCode,
    address: addressVO?.departure_address,
    addressTwo: addressVO?.departure_addressTwo,
    townName: addressVO?.departure_town || undefined,
    name: addressVO?.departure_fullName,
    mobile: addressVO?.departure_mobile,
  }

  if (type !== 'batch') {
    address['consigneeAddressName'] = {
      countryCode: addressVO?.arrival_countryCode,
      provinceName: isHome ? addressVO?.arrival_province : addressVO?.arrival_store_province,
      cityName: isHome ? addressVO?.arrival_city : addressVO?.arrival_store_city,
      districtName: isHome ? addressVO?.arrival_district : addressVO.arrival_store_district,
      townName: isHome ? addressVO?.arrival_town : undefined,
      zipCode: addressVO?.arrival_zipCode,
      address: addressVO?.arrival_address,
      addressTwo: addressVO?.arrival_addressTwo,
      name: addressVO?.arrival_fullName,
      mobile: addressVO?.arrival_mobile,
    }
  }

  return address
}

export const getServiceProvider = async (payload: IGetServiceProvider): Promise<ExpressListData[]> => {
  const { props, currencyCode, isPrepaid, type, orderCodCurrencyCode, provider_pickup_type, partitionInfo } = payload
  const { parcel_weight, parcel_weight_unit, parcel_height, parcel_length, parcel_width, supportedAddressTypes, addressVO: inputAddressVO, ...args } = props ?? {}
  /**
   * 是宅配
   */
  const isHome = supportedAddressTypes?.[0] === 'HOME_STREET'

  const addressVO = convertAddress({
    addressVO: inputAddressVO,
    type,
    isHome,
  })

  const extraStationProps = isHome
    ? {}
    : {
        stationCode: inputAddressVO.arrival_station_code,
        stationType: inputAddressVO.arrival_station_type,
      }

  const restParams = omit(args, ['appId', 'arrival_method', 'orderInfo'])

  const params = {
    ...restParams,
    companyCode: isHome ? undefined : restParams?.companyCode,
    needStationType: true,
    supportedAddressTypes,
    queryDeliveryDate: true,
    pageSize: 999,
    pageNum: 1,
    weightUnit: (parcel_weight_unit || 'kg') as 'kg',
    weight: Number(parcel_weight),
    length: Number(parcel_length),
    width: Number(parcel_width),
    height: Number(parcel_height),
    lengthUnit: 'cm',
    orderCodCurrencyCode,
    ...addressVO,
    ...extraStationProps,
  }

  try {
    const data = await getExpressList(params)
    const expressList = data.data.data

    if (expressList && expressList.length) {
      const tmpParams = expressList.slice().map((item) => {
        // the priority is the pickup
        const pickupType = getPickupType({
          supportedPickupTypes: item.supportedPickupTypes,
          provider_pickup_type,
        })
        return {
          expressTypeCode: item.expressTypeCode,
          metadatas: [
            {
              metadataId: Math.floor(Math.random() * Math.pow(10, 20)) + '',
              weightUnit: parcel_weight_unit || 'kg',
              length: Number(parcel_length),
              width: Number(parcel_width),
              height: Number(parcel_height),
              lengthUnit: 'cm',
              weight: parcel_weight?.toString(),
              currencyCode,
              senderAddress: addressVO?.senderAddressName,
              consigneeAddress: addressVO?.consigneeAddressName,
              partitionInfo: {
                ...partitionInfo,
                pickupType: pickupType,
                deliveryType: isHome ? DELIVER_VALUE_MAP.HOME : DELIVER_VALUE_MAP.STORE,
              },
            },
          ],
        }
      })

      let newExpressList = expressList

      // 非批量需要计算运费
      if (type !== 'batch') {
        const feeRes = await getFeeList({
          feeMetadatas: tmpParams,
        })

        const currentFeeArr = feeRes?.data?.shippingFeeResults ?? []

        const feeMap = new Map(currentFeeArr.map((item) => [item.expressTypeCode, item]))

        newExpressList = expressList
          ?.slice()
          ?.map((item) => {
            const { expressTypeCode } = item

            const currentData = feeMap.get(expressTypeCode)

            const currentFeeData = currentData?.shippingResults?.[0]
            const rightsResults = currentData?.rightsResults

            let promisedTime = item.promisedTime
            let planName = ''
            if (currentFeeData?.channelCustomParamParse) {
              const customData = currentFeeData.channelCustomParamParse
              promisedTime = customData.arriveDaysRange || promisedTime
              planName = customData[item.companyCode.toLocaleLowerCase()]?.planName
            }

            return {
              ...item,
              estimatedCost: currentFeeData?.freight,
              freight: currentFeeData?.freight,
              freightCurrencyCode: currentFeeData?.currencyCode,
              originalFreight: currentFeeData?.originalFreight,
              discountFreight: currentFeeData?.discountFreight,
              rightsResults,
              failReason: currentFeeData?.failReason,
              promisedTime,
              planName,
            }
          })
          .map((item) => ({
            ...item,
            fee: `${item.estimatedCost ? translateCurrency(item.estimatedCost) : ''} ${''}`,
          }))
          .filter((item) => {
            // saas服务产品需要展示
            if (item.saasFlag === SAAS_FLAG.YES) {
              return true
            }

            // 当用户是个人用户时  如果运费试算不能计算出运费，或者运费为负数或者0，不显示这个服务产品给用户看到
            // https://shopline.yuque.com/slpm/ta6d4y/nmwb7s#j8733
            if (isPrepaid) {
              //支持到付的服务产品可展示，排除zeek
              if (item.settlementModes.includes(2)) {
                if (!isZEEKFn(item.companyCode)) {
                  return true
                }
              }

              return !!item.estimatedCost
            }
            return true
          })
      }

      if (newExpressList && newExpressList.length) {
        return sortProviderList(newExpressList)
      } else {
        Toast.show({
          content: i18n.t('newOrder.noMatch'),
          icon: 'fail',
        })
        return []
      }
    }
  } catch (error) {
    throw error
  }

  return []
}

export const convertAddress2Props = (address: ICoverAddressTypes): IProviderAddressVO => {
  return {
    departure_countryCode: address.senderCountryCode,
    departure_city: address.senderCity,
    departure_province: address.senderProvince,
    departure_district: address.senderDistrict,
    departure_town: address.senderTown,
    departure_zipCode: address.senderZipCode,
    departure_address: address.senderAddress,
    departure_addressTwo: address.senderAddressTwo,
    departure_fullName: address.senderName,
    departure_mobile: address.senderMobile,
    arrival_countryCode: address.consigneeCountryCode,
    arrival_city: address.consigneeCity,
    arrival_district: address.consigneeDistrict,
    arrival_province: address.consigneeProvince,
    arrival_town: address.consigneeTown,
    arrival_zipCode: address.consigneeZipCode,
    arrival_address: address.consigneeAddress,
    arrival_addressTwo: address.consigneeAddressTwo,
    arrival_fullName: address.consigneeName,
    arrival_mobile: address.consigneeMobile,
  }
}

/** 获取派送时间 */
export const getDeliveryTime = ({ provider, formValues }: { provider: Partial<ExpressListData>; formValues: { provider_delivery_dates?: string[] } }) => {
  const selectableDeliveryTimes = provider?.selectableDeliveryList?.selectableDeliveryTimes
  const { provider_delivery_dates } = formValues
  const [provider_delivery_date, provider_delivery_time] = provider_delivery_dates || []
  const targetDate = selectableDeliveryTimes?.find((item) => item.date === provider_delivery_date)
  const res = {
    providerDeliveryDateOptions: selectableDeliveryTimes,
    providerDeliveryTimeOptions: [],
    provider_delivery_dates: [],
  }
  if (targetDate) {
    res.provider_delivery_dates = [targetDate.date, targetDate.timeIntervals?.find((item) => item === provider_delivery_time) || '']
    res.providerDeliveryTimeOptions = selectableDeliveryTimes?.find((item) => item.date === provider_delivery_date)?.timeIntervals || []
  }
  return res
}

interface ISortProviderListOptions {
  /** this option will filter the ones who available is false */
  filter?: boolean
}
export function sortProviderList(list?: ExpressListData[], options?: ISortProviderListOptions) {
  if (!list?.length) return []

  const { filter = false } = options || ({} as ISortProviderListOptions)

  const newList = Array.from(list)?.sort((a, b) => {
    if (a.available && !b.available) return -1
    if (!a.available && b.available) return 1
    return 0
  })

  if (filter) {
    return newList?.filter((item) => item.available)
  }

  return newList
}

export const calculateFreightAndFeeItem = async (feeMetadatas) => {
  const { data } = await getFeeList({
    feeMetadatas,
  })

  const feeRes = data?.shippingFeeResults?.[0]

  return feeRes
}

export const formatFreightItem = (currentData: FeeObject) => {
  const currentFeeData = currentData?.shippingResults?.[0]
  const rightsResults = currentData?.rightsResults

  return {
    estimatedCost: currentFeeData?.freight,
    freight: currentFeeData?.freight,
    freightCurrencyCode: currentFeeData?.currencyCode,
    originalFreight: currentFeeData?.originalFreight,
    discountFreight: currentFeeData?.discountFreight,
    rightsResults,
    failReason: currentFeeData?.failReason,
    feeItems: currentFeeData?.feeItems,
  }
}

interface calculateTotalPricePrams {
  /** 包裹信息 */
  parcel_commodities: ParcelFormFields['parcel_commodities']
  /** 服务产品运费币种 */
  // freightCurrencyCode: string
  // /** 订单币种 */
  // currencyCode: string
  // /** 最大投保额 */
  // maxInsuredAmount?: number | undefined
  /**
   * 是否开启了币种自动转换
   */
  // hasCurrencyRate: boolean
}

/**
 * 计算前端商品总额
 * @returns
 */
export const calculateTotalPrice: (params: calculateTotalPricePrams) => Promise<number | undefined> = async (params) => {
  const { parcel_commodities } = params
  if (parcel_commodities?.length) {
    let totalPrice = parcel_commodities.reduce((pre, next) => {
      const price = times(next.parcel_goods_price, next.parcel_goods_quantity)
      return price + pre
    }, 0)
    totalPrice = times(Number(totalPrice), 100)

    totalPrice = typeof totalPrice === 'number' ? totalPrice : 0
    return totalPrice
  }
}
